import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { hasFeature } from 'src/utils/userUtils';
import { ActionOutboxEntityType, Feature } from 'src/WebApiController';

export const useUserHasFeature = (feature: Feature | null | undefined) => {
  const { appContext, loginContext } = useAppContext();

  const hasFeatureResult = useMemo(
    () => hasFeature(loginContext?.user, appContext?.features, feature),
    [appContext?.features, feature, loginContext?.user]
  );

  return hasFeatureResult;
};

export const useUserHasGrpcCache = () => {
  const { loginContext } = useAppContext();

  return loginContext?.user?.activeAccount?.isCached;
};

export const useUserHasTableTotalsFeature = (
  entityType?: ActionOutboxEntityType
) => {
  const { mainRoute } = useAppContext();

  const hasTableTotalsFeature = useUserHasFeature(Feature.TableTotals_All);

  const isSearchPage = [
    MainRoute.InventorySearch,
    MainRoute.InventorySearchV2,
    MainRoute.SalesSearch,
    MainRoute.SalesSearchV2,
    MainRoute.PurchaseSearch,
    MainRoute.PurchaseSearchV2,
  ].includes(mainRoute);

  const hasTableTotalsSpecificFeature = useUserHasFeature(
    // Use entityType to override when explicitly provided - it affects the display of side tables (for example, Inventory table in Sales Event)
    entityType === ActionOutboxEntityType.Listing
      ? Feature.TableTotals_Inventory
      : entityType === ActionOutboxEntityType.Sale
        ? Feature.TableTotals_Sales
        : entityType === ActionOutboxEntityType.Purchase
          ? Feature.TableTotals_Purchase
          : entityType === ActionOutboxEntityType.SellerEvent
            ? Feature.TableTotals_Events
            : [MainRoute.Inventory, MainRoute.InventoryEvent].includes(
                  mainRoute
                )
              ? Feature.TableTotals_Inventory
              : [MainRoute.Sales, MainRoute.SalesEvent].includes(mainRoute)
                ? Feature.TableTotals_Sales
                : [MainRoute.Purchases, MainRoute.PurchasesEvent].includes(
                      mainRoute
                    )
                  ? Feature.TableTotals_Purchase
                  : [MainRoute.Payments].includes(mainRoute)
                    ? Feature.TableTotals_Payments
                    : Feature.TableTotals_All
  );

  return hasTableTotalsFeature || hasTableTotalsSpecificFeature || isSearchPage;
};

export const useRefetchCatalogAndEntityOnNavigateFeature = (
  entityType?: ActionOutboxEntityType
) => {
  const { mainRoute } = useAppContext();

  const hasAllFeature = useUserHasFeature(
    Feature.RefetchCatalogAndEntityOnNavigate_All
  );

  const hasEntitySpecificFeature = useUserHasFeature(
    // Use entityType to override when explicitly provided - it affects the display of side tables (for example, Inventory table in Sales Event)
    entityType === ActionOutboxEntityType.Listing
      ? Feature.RefetchCatalogAndEntityOnNavigate_Inventory
      : entityType === ActionOutboxEntityType.Sale
        ? Feature.RefetchCatalogAndEntityOnNavigate_Sales
        : entityType === ActionOutboxEntityType.Purchase
          ? Feature.RefetchCatalogAndEntityOnNavigate_Purchase
          : [MainRoute.Inventory, MainRoute.InventoryEvent].includes(mainRoute)
            ? Feature.RefetchCatalogAndEntityOnNavigate_Inventory
            : [MainRoute.Sales, MainRoute.SalesEvent].includes(mainRoute)
              ? Feature.RefetchCatalogAndEntityOnNavigate_Sales
              : [MainRoute.Purchases, MainRoute.PurchasesEvent].includes(
                    mainRoute
                  )
                ? Feature.RefetchCatalogAndEntityOnNavigate_Purchase
                : Feature.RefetchCatalogAndEntityOnNavigate_All
  );

  return hasAllFeature || hasEntitySpecificFeature;
};
